import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Heart,
  Share2,
  MessageCircle,
  DollarSign,
  X,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  Maximize,
  Minimize,
  ChevronLeft,
  ChevronRight,
  Flame,
  Laugh,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const EpisodeReader = () => {
  const { seriesId, chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Core data
  const [series, setSeries] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Reader state
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.sessionStorage.getItem('episode_view_mode');
      if (saved === 'vertical' || saved === 'horizontal' || saved === 'paged') {
        return saved;
      }
    }
    return 'vertical';
  }); // 'vertical' | 'horizontal' | 'paged'
  const [currentPage, setCurrentPage] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  
  // Interaction state
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [commentSort, setCommentSort] = useState('newest');
  const [showTipModal, setShowTipModal] = useState(false);
  
  // Engagement features
  const [seriesProgress, setSeriesProgress] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [topComments, setTopComments] = useState([]);
  const [topFans, setTopFans] = useState([]);
  const [following, setFollowing] = useState(false);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [newAchievements, setNewAchievements] = useState([]);
  
  // Refs
  const readerContainerRef = useRef(null);
  const hideControlsTimerRef = useRef(null);
  const progressSaveTimerRef = useRef(null);

  useEffect(() => {
    if (seriesId && chapterId) {
      fetchChapterData();
    }
  }, [seriesId, chapterId]);

  useEffect(() => {
    if (currentChapter) {
      // Determine view mode: prefer saved session mode, then series.reading_mode, then format fallback
      let initialMode = 'vertical';
      try {
        if (typeof window !== 'undefined') {
          const saved = window.sessionStorage.getItem('episode_view_mode');
          if (saved === 'vertical' || saved === 'horizontal' || saved === 'paged') {
            initialMode = saved;
          } else {
            // Derive from series metadata
            const readingMode = series?.reading_mode;
            if (readingMode === 'vertical') {
              initialMode = 'vertical';
            } else if (readingMode === 'horizontal_scroll') {
              initialMode = 'horizontal';
            } else if (readingMode === 'paged' || readingMode === 'horizontal') {
              // 'horizontal' is legacy value for page-turn mode
              initialMode = 'paged';
            } else {
              const format = series?.format || 'webtoon';
              initialMode = format === 'webtoon' ? 'vertical' : 'paged';
            }
          }
        }
      } catch (e) {
        const readingMode = series?.reading_mode;
        if (readingMode === 'vertical') {
          initialMode = 'vertical';
        } else if (readingMode === 'horizontal_scroll') {
          initialMode = 'horizontal';
        } else if (readingMode === 'paged' || readingMode === 'horizontal') {
          initialMode = 'paged';
        } else {
          const format = series?.format || 'webtoon';
          initialMode = format === 'webtoon' ? 'vertical' : 'paged';
        }
      }
      setViewMode(initialMode);
      
      // Record view
      recordView();
      
      // Load saved progress if user is logged in
      if (user) {
        loadProgress();
        checkLikeStatus();
      }
      
      // Load comments and engagement data
      loadComments();
      loadEngagementData();
    }
  }, [currentChapter, user, series]);

  // Scroll tracking for vertical mode
  useEffect(() => {
    if (viewMode === 'vertical' && readerContainerRef.current) {
      const handleScroll = () => {
        const container = readerContainerRef.current;
        if (container) {
          const scrolled = container.scrollTop;
          const total = container.scrollHeight - container.clientHeight;
          const progress = (scrolled / total) * 100;
          setScrollProgress(progress);
          
          // Auto-hide controls
          setControlsVisible(true);
          clearTimeout(hideControlsTimerRef.current);
          hideControlsTimerRef.current = setTimeout(() => {
            setControlsVisible(false);
          }, 3000);
          
          // Save progress periodically
          if (user) {
            clearTimeout(progressSaveTimerRef.current);
            progressSaveTimerRef.current = setTimeout(() => {
              const completed = progress >= 95;
              saveProgress(scrolled, currentPage, completed);
              
              // Show completion screen if just completed
              if (completed && !showCompletionScreen) {
                setShowCompletionScreen(true);
                checkAchievements();
              }
            }, 2000);
          }
        }
      };

      const container = readerContainerRef.current;
      container.addEventListener('scroll', handleScroll);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        clearTimeout(hideControlsTimerRef.current);
        clearTimeout(progressSaveTimerRef.current);
      };
    }
  }, [viewMode, currentPage, user]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (viewMode === 'vertical') {
        // Arrow keys for scrolling
        if (e.key === 'ArrowDown') {
          readerContainerRef.current?.scrollBy({ top: 300, behavior: 'smooth' });
        } else if (e.key === 'ArrowUp') {
          readerContainerRef.current?.scrollBy({ top: -300, behavior: 'smooth' });
        }
      } else {
        // Arrow keys for pagination
        if (e.key === 'ArrowRight') {
          nextPage();
        } else if (e.key === 'ArrowLeft') {
          prevPage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, currentPage]);

  const fetchChapterData = async () => {
    try {
      const [seriesRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/series/${seriesId}`),
        axios.get(`${API}/series/${seriesId}/chapters`)
      ]);

      setSeries(seriesRes.data);
      setChapters(chaptersRes.data);

      const chapter = chaptersRes.data.find(ch => ch.id === chapterId);
      if (chapter) {
        setCurrentChapter(chapter);
      } else {
        toast.error('Chapter not found');
        navigate(`/series/${seriesId}`);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching chapter:', error);
      toast.error('Failed to load chapter');
      setLoading(false);
    }
  };

  const recordView = async () => {
    try {
      await axios.post(`${API}/chapters/${chapterId}/view`);
    } catch (error) {
      console.error('Error recording view:', error);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${chapterId}/progress`);
      const progress = response.data;
      
      if (progress.scroll_position > 0) {
        setTimeout(() => {
          if (readerContainerRef.current) {
            readerContainerRef.current.scrollTop = progress.scroll_position;
          }
        }, 100);
      }
      
      if (progress.page_number > 0) {
        setCurrentPage(progress.page_number);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProgress = async (scrollPos, pageNum, completed) => {
    if (!user) return;
    
    try {
      await axios.post(`${API}/chapters/${chapterId}/progress`, {
        chapter_id: chapterId,
        series_id: seriesId,
        scroll_position: scrollPos || 0,
        page_number: pageNum || 0,
        completed: completed || false
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const checkLikeStatus = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${chapterId}/like-status`);
      setLiked(response.data.liked);
      setLikeCount(response.data.like_count);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like episodes');
      return;
    }

    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/like`);
      setLiked(response.data.liked);
      setLikeCount(prev => response.data.liked ? prev + 1 : prev - 1);
      toast.success(response.data.liked ? 'Liked! ❤️' : 'Unliked');
    } catch (error) {
      console.error('Error liking chapter:', error);
      toast.error('Failed to like episode');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `${series?.title} - ${currentChapter?.title}`,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const loadComments = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${chapterId}/comments?sort=${commentSort}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadEngagementData = async () => {
    try {
      const [progressRes, recommendationsRes, topCommentsRes, topFansRes] = await Promise.all([
        user ? axios.get(`${API}/series/${seriesId}/reading-progress`) : Promise.resolve({ data: null }),
        axios.get(`${API}/series/${seriesId}/recommendations?limit=6`),
        axios.get(`${API}/chapters/${chapterId}/top-comments?limit=3`),
        axios.get(`${API}/series/${seriesId}/top-fans?limit=5`)
      ]);

      if (user && progressRes.data) {
        setSeriesProgress(progressRes.data);
      }
      setRecommendations(recommendationsRes.data);
      setTopComments(topCommentsRes.data);
      setTopFans(topFansRes.data);

      // Load creator profile for follow button
      if (series?.profile_id) {
        const profileRes = await axios.get(`${API}/creator-profiles/${series.profile_id}`);
        setCreatorProfile(profileRes.data);
        
        if (user) {
          const followRes = await axios.get(`${API}/creators/${series.profile_id}/follow-status`);
          setFollowing(followRes.data.following);
        }
      }
    } catch (error) {
      console.error('Error loading engagement data:', error);
    }
  };

  const checkAchievements = async () => {
    if (!user) return;
    
    try {
      const response = await axios.post(`${API}/achievements/check`, {
        series_id: seriesId
      });
      
      if (response.data.new_achievements && response.data.new_achievements.length > 0) {
        setNewAchievements(response.data.new_achievements);
        // Show achievement toast
        response.data.new_achievements.forEach(achievement => {
          toast.success(`Achievement Unlocked: ${achievement.icon} ${achievement.title}!`, {
            duration: 5000
          });
        });
      }
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error('Please login to follow creators');
      return;
    }

    try {
      const response = await axios.post(`${API}/creators/${series.profile_id}/follow`);
      setFollowing(response.data.following);
      toast.success(response.data.following ? 'Following creator! 🎨' : 'Unfollowed');
    } catch (error) {
      console.error('Error following creator:', error);
      toast.error('Failed to follow creator');
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/comments`, {
        content: newComment
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    }
  };

  const nextPage = () => {
    const pages = currentChapter?.pages_data || [];
    if (currentPage < pages.length - 1) {
      setCurrentPage(prev => prev + 1);
      if (user) {
        saveProgress(0, currentPage + 1, currentPage + 1 === pages.length - 1);
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      if (user) {
        saveProgress(0, currentPage - 1, false);
      }
    }
  };

  const nextChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === chapterId);
    if (currentIndex < chapters.length - 1) {
      const nextChap = chapters[currentIndex + 1];
      navigate(`/series/${seriesId}/read/${nextChap.id}`);
    }
  };

  const prevChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === chapterId);
    if (currentIndex > 0) {
      const prevChap = chapters[currentIndex - 1];
      navigate(`/series/${seriesId}/read/${prevChap.id}`);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleSliderChange = (value) => {
    if (viewMode === 'vertical' && readerContainerRef.current) {
      const container = readerContainerRef.current;
      const total = container.scrollHeight - container.clientHeight;
      container.scrollTop = (value[0] / 100) * total;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading episode...</div>
        </div>
      </div>
    );
  }

  if (!currentChapter) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Episode not found</h2>
          <Link to={`/series/${seriesId}`}>
            <Button className="bg-orange-500 hover:bg-orange-600">Back to Series</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pages = currentChapter.pages_data || [];
  const currentIndex = chapters.findIndex(ch => ch.id === chapterId);
  const hasNextChapter = currentIndex < chapters.length - 1;
  const hasPrevChapter = currentIndex > 0;

  return (
    <div className="relative h-screen bg-black overflow-hidden">
      {/* Top Control Bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/90 to-transparent transition-all duration-300 ${
          controlsVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={`/series/${seriesId}`}>
              <Button variant="ghost" size="sm" className="text-white hover:text-orange-400">
                <X className="w-5 h-5" />
              </Button>
            </Link>
            <div className="hidden md:block">
            {/* Reader Mode Toggle */}
            <div className="flex items-center space-x-2">
              {['vertical', 'horizontal', 'paged'].map(mode => (
                <Button
                  key={mode}
                  variant={viewMode === mode ? 'default' : 'ghost'}
                  size="sm"
                  className={`text-xs ${viewMode === mode ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'text-gray-300 hover:text-white'}`}
                  onClick={() => {
                    setViewMode(mode);
                    try {
                      if (typeof window !== 'undefined') {
                        window.sessionStorage.setItem('episode_view_mode', mode);
                      }
                    } catch (e) {
                      // ignore
                    }
                  }}
                >
                  {mode === 'vertical' && 'Vertical'}
                  {mode === 'horizontal' && 'Horizontal'}
                  {mode === 'paged' && 'Pages'}
                </Button>
              ))}
            </div>

              <h1 className="text-white font-bold text-lg">{series?.title}</h1>
              <p className="text-gray-400 text-sm">Episode {currentChapter.chapter_number}: {currentChapter.title}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBookmarked(!bookmarked)}
              className="text-white hover:text-orange-400"
            >
              {bookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-white hover:text-orange-400 hidden md:block"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Reader Container */}
      {viewMode === 'vertical' && (
        <div
          ref={readerContainerRef}
          className="h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
          onClick={() => setControlsVisible(!controlsVisible)}
        >
          <div className="pt-16 pb-4">
            <div className="max-w-3xl mx-auto bg-black">
              {pages.map((page, index) => (
                <img
                  key={index}
                  src={BACKEND_URL + page.url}
                  alt={`Page ${index + 1}`}
                  className="w-full h-auto block"
                  style={{ margin: 0, padding: 0, display: 'block', lineHeight: 0 }}
                  loading={index < 2 ? 'eager' : 'lazy'}
                />
              ))}

              {/* Series Progress Section */}
              {user && seriesProgress && (
                <div className="py-8 border-t border-gray-800">
                  <Card className="bg-gradient-to-r from-gray-900 to-gray-800 border-orange-500/30">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-white mb-4">📊 Your Progress</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between text-gray-300">
                          <span>Episodes Completed</span>
                          <span className="font-bold text-orange-400">
                            {seriesProgress.completed_episodes} / {seriesProgress.total_episodes}
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${seriesProgress.percentage}%` }}
                          />
                        </div>
                        <p className="text-center text-gray-400 text-sm">
                          {seriesProgress.percentage}% complete
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Top Comments Spotlight */}
              {topComments.length > 0 && (
                <div className="py-8 border-t border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">💬 Top Comments</h3>
                  <div className="space-y-4">
                    {topComments.map((comment) => (
                      <Card key={comment.id} className="bg-gray-900 border-gray-800">
                        <CardContent className="p-4">
                          <div className="flex items-start space-x-3">
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-orange-500 text-white">
                                {comment.username?.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-white font-medium">{comment.username}</span>
                                <span className="text-orange-400 text-xs">⭐ Top Fan</span>
                              </div>
                              <p className="text-gray-300 mb-2">{comment.content}</p>
                              <div className="flex items-center space-x-3 text-sm">
                                <span className="flex items-center text-orange-400">
                                  <Flame className="w-4 h-4 mr-1" />
                                  {comment.reactions?.fire || 0}
                                </span>
                                <span className="flex items-center text-red-400">
                                  <Heart className="w-4 h-4 mr-1" />
                                  {comment.reactions?.heart || 0}
                                </span>
                                <span className="flex items-center text-yellow-400">
                                  <Laugh className="w-4 h-4 mr-1" />
                                  {comment.reactions?.laugh || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Creator Section */}
              {creatorProfile && (
                <div className="py-8 border-t border-gray-800">
                  <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-white">🎨 Support the Creator</h3>
                        <Button
                          onClick={handleFollow}
                          className={`${
                            following
                              ? 'bg-gray-700 hover:bg-gray-600'
                              : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                          } text-white font-bold`}
                        >
                          {following ? '✓ Following' : '+ Follow'}
                        </Button>
                      </div>
                      <div className="flex items-center space-x-4 mb-4">
                        <Avatar className="w-16 h-16 border-2 border-orange-500">
                          <AvatarFallback className="bg-orange-500 text-white text-xl">
                            {creatorProfile.display_name?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-bold text-lg">{creatorProfile.display_name}</p>
                          <p className="text-gray-400">@{creatorProfile.handle}</p>
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <Button
                          onClick={() => setShowTipModal(true)}
                          className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Tip Creator
                        </Button>
                        <Button
                          onClick={handleShare}
                          variant="outline"
                          className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Top Fans Leaderboard */}
              {topFans.length > 0 && (
                <div className="py-8 border-t border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">👑 Top Fans</h3>
                  <div className="space-y-3">
                    {topFans.map((fan, index) => (
                      <div
                        key={fan.user_id}
                        className="flex items-center justify-between bg-gray-900 p-4 rounded-lg border border-gray-800"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            index === 0 ? 'bg-yellow-500 text-black' :
                            index === 1 ? 'bg-gray-400 text-black' :
                            index === 2 ? 'bg-orange-600 text-white' :
                            'bg-gray-700 text-gray-300'
                          }`}>
                            {index + 1}
                          </div>
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-orange-500 text-white">
                              {fan.username?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-white font-medium">{fan.username}</span>
                        </div>
                        <span className="text-orange-400 font-bold">{fan.score} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Series */}
              {recommendations.length > 0 && (
                <div className="py-8 border-t border-gray-800">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white">🔥 You May Also Like</h3>
                    <Link to="/browse" className="text-orange-400 hover:text-orange-300 text-sm font-medium">
                      View All →
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {recommendations.map((rec) => (
                      <Link key={rec.id} to={`/series/${rec.id}`} className="group">
                        <div className="relative aspect-[2/3] bg-gray-800 rounded-lg overflow-hidden mb-2">
                          {rec.cover_url ? (
                            <img
                              src={rec.cover_url}
                              alt={rec.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-600">
                              No Image
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <h4 className="text-white font-medium text-sm line-clamp-2 mb-1 group-hover:text-orange-400 transition-colors">
                          {rec.title}
                        </h4>
                        <p className="text-gray-400 text-xs line-clamp-1">{rec.genres?.slice(0, 2).join(', ')}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Chapter Button */}
              {hasNextChapter && (
                <div className="py-12 text-center border-t border-gray-800">
                  <Button
                    onClick={nextChapter}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-8 py-6 text-lg"
                  >
                    Next Episode <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}

              {/* End of Episode Message */}
              {!hasNextChapter && (
                <div className="py-12 text-center border-t border-gray-800">
                  <p className="text-gray-400 mb-4">You've reached the latest episode!</p>
                  <Link to={`/series/${seriesId}`}>
                    <Button className="bg-orange-500 hover:bg-orange-600">
                      Back to Series
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Horizontal Scroll Mode */}
      {viewMode === 'horizontal' && (
        <div
          ref={readerContainerRef}
          className="h-full overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
          onClick={() => setControlsVisible(!controlsVisible)}
        >
          <div className="pt-16 pb-4 h-full">
            <div className="flex flex-row items-stretch h-full px-4 space-x-4">
              {pages.map((page, index) => (
                <div key={index} className="flex-shrink-0 flex items-center">
                  <img
                    src={BACKEND_URL + page.url}
                    alt={`Page ${index + 1}`}
                    className="h-[80vh] w-auto object-contain"
                    style={{ lineHeight: 0 }}
                    loading={index < 2 ? 'eager' : 'lazy'}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Paged/Page-turn Mode */}
      {viewMode === 'paged' && (
        // Horizontal Page Mode
        <div className="h-full flex items-center justify-center relative" onClick={() => setControlsVisible(!controlsVisible)}>
          <img
            src={pages[currentPage] ? BACKEND_URL + pages[currentPage].url : ''}
            alt={`Page ${currentPage + 1}`}
            className="max-h-full max-w-full object-contain"
          />

          {/* Page Navigation */}
          {currentPage > 0 && (
            <button
              onClick={prevPage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {currentPage < pages.length - 1 && (
            <button
              onClick={nextPage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Page Counter */}
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm">
            Page {currentPage + 1} of {pages.length}
          </div>
        </div>
      )}

      {/* Progress Bar (Vertical Mode Only) */}
      {viewMode === 'vertical' && (
        <div className="fixed right-2 top-20 bottom-20 w-1 bg-gray-800 rounded-full z-40">
          <div
            className="bg-orange-500 rounded-full transition-all duration-300"
            style={{ height: `${scrollProgress}%` }}
          />
        </div>
      )}

      {/* Bottom Control Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/90 to-transparent transition-all duration-300 ${
          controlsVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          {/* Quick Navigation Slider (Vertical Mode) */}
          {viewMode === 'vertical' && (
            <div className="mb-4 hidden md:block">
              <Slider
                value={[scrollProgress]}
                onValueChange={handleSliderChange}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLike}
                className={`${liked ? 'text-red-500' : 'text-white'} hover:text-red-400`}
              >
                <Heart className={`w-5 h-5 mr-1 ${liked ? 'fill-red-500' : ''}`} />
                <span>{likeCount}</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowComments(!showComments)}
                className="text-white hover:text-orange-400"
              >
                <MessageCircle className="w-5 h-5 mr-1" />
                <span className="hidden md:inline">Comments</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="text-white hover:text-orange-400"
              >
                <Share2 className="w-5 h-5" />
              </Button>

              <Dialog open={showTipModal} onOpenChange={setShowTipModal}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-yellow-400 hover:text-yellow-300">
                    <DollarSign className="w-5 h-5 mr-1" />
                    <span className="hidden md:inline">Tip</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Support the Creator</DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Show your appreciation for this amazing work!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                      $2 - Coffee
                    </Button>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
                      $5 - Support
                    </Button>
                    <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-bold">
                      $10 - Super Fan
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Chapter Navigation */}
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevChapter}
                disabled={!hasPrevChapter}
                className="text-white hover:text-orange-400 disabled:opacity-30"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="hidden md:inline">Prev</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={nextChapter}
                disabled={!hasNextChapter}
                className="text-white hover:text-orange-400 disabled:opacity-30"
              >
                <span className="hidden md:inline">Next</span>
                <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Overlay */}
      {showComments && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowComments(false)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-full md:w-96 bg-gray-900 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Comments ({comments.length})</h3>
                <button onClick={() => setShowComments(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Add Comment */}
              {user ? (
                <div className="mb-6">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    className="bg-gray-800 border-gray-700 text-white mb-3"
                    rows={3}
                  />
                  <Button
                    onClick={handleAddComment}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    Post Comment
                  </Button>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-800 rounded-lg text-center">
                  <p className="text-gray-400 mb-3">Login to comment</p>
                  <Link to="/auth">
                    <Button className="bg-orange-500 hover:bg-orange-600">Login</Button>
                  </Link>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-4">
                {comments.map((comment) => (
                  <Card key={comment.id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-orange-500 text-white">
                            {comment.username?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-white font-medium">{comment.username}</span>
                            <span className="text-gray-500 text-sm">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-300 mb-3">{comment.content}</p>
                          <div className="flex items-center space-x-4 text-sm">
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-orange-400">
                              <Flame className="w-4 h-4" />
                              <span>{comment.reactions?.fire || 0}</span>
                            </button>
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-red-400">
                              <Heart className="w-4 h-4" />
                              <span>{comment.reactions?.heart || 0}</span>
                            </button>
                            <button className="flex items-center space-x-1 text-gray-400 hover:text-yellow-400">
                              <Laugh className="w-4 h-4" />
                              <span>{comment.reactions?.laugh || 0}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {comments.length === 0 && (
                  <div className="text-center py-12">
                    <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No comments yet. Be the first!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Screen Overlay */}
      {showCompletionScreen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={() => setShowCompletionScreen(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-black text-white mb-2">Episode Complete!</h2>
              <p className="text-gray-400">
                Episode {currentChapter?.chapter_number}: {currentChapter?.title}
              </p>
            </div>

            {/* New Achievements */}
            {newAchievements.length > 0 && (
              <div className="mb-6 p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg border border-yellow-500/50">
                <h3 className="text-yellow-400 font-bold mb-3 flex items-center">
                  🏆 New Achievements Unlocked!
                </h3>
                <div className="space-y-2">
                  {newAchievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-center space-x-2 text-white">
                      <span className="text-2xl">{achievement.icon}</span>
                      <span className="font-medium">{achievement.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Series Progress */}
            {seriesProgress && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Series Progress</span>
                  <span className="text-orange-400 font-bold">
                    {seriesProgress.completed_episodes}/{seriesProgress.total_episodes}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full"
                    style={{ width: `${seriesProgress.percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleLike}
                className={`w-full ${
                  liked
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-gray-800 hover:bg-gray-700'
                } text-white font-bold`}
              >
                <Heart className={`w-5 h-5 mr-2 ${liked ? 'fill-white' : ''}`} />
                {liked ? 'Liked!' : 'Like This Episode'}
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => {
                    setShowCompletionScreen(false);
                    setShowComments(true);
                  }}
                  variant="outline"
                  className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Comment
                </Button>
                <Button
                  onClick={() => {
                    setShowCompletionScreen(false);
                    setShowTipModal(true);
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Tip
                </Button>
              </div>

              {hasNextChapter ? (
                <Button
                  onClick={() => {
                    setShowCompletionScreen(false);
                    nextChapter();
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg py-6"
                >
                  Next Episode <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Link to={`/series/${seriesId}`} className="block">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6">
                    Back to Series
                  </Button>
                </Link>
              )}

              <button
                onClick={() => setShowCompletionScreen(false)}
                className="w-full text-gray-400 hover:text-white text-sm py-2"
              >
                Continue Reading Below
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpisodeReader;
